import { useState, useEffect, useRef, type CSSProperties } from 'react';
import { ChevronDown } from 'lucide-react';
import { useT } from '../i18n';

interface DropdownSelectProps {
  value: string | number;
  options: { value: string | number; label: string }[];
  placeholder?: string;
  onChange: (val: string | number) => void;
  triggerStyle?: CSSProperties;
  dropdownStyle?: CSSProperties;
}

export function DropdownSelect({ value, options, placeholder, onChange, triggerStyle, dropdownStyle }: DropdownSelectProps) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selectedLabel = options.find(o => String(o.value) === String(value))?.label || placeholder || t('common.selectEllipsis');

  return (
    <div className="autosuggest-wrapper" ref={ref}>
      <div
        className="form-input"
        style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', ...triggerStyle }}
        onClick={() => setOpen(!open)}
      >
        <span style={{ color: value ? 'var(--ink-1)' : 'var(--ink-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selectedLabel}
        </span>
        <ChevronDown size={14} style={{ color: 'var(--ink-3)', flexShrink: 0 }} />
      </div>
      {open && (
        <div className="autosuggest-dropdown" style={dropdownStyle}>
          {placeholder && (
            <div className="autosuggest-option" style={{ color: 'var(--ink-3)' }} onClick={() => { onChange(''); setOpen(false); }}>
              {placeholder}
            </div>
          )}
          {options.map((o) => (
            <div
              key={o.value}
              className="autosuggest-option"
              style={{ fontWeight: String(o.value) === String(value) ? 600 : 400 }}
              onClick={() => { onChange(o.value); setOpen(false); }}
            >
              {o.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
